import type React from 'react';
import { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import FocusLock from 'react-focus-lock';

import appUtils from 'app/shared/utils/appUtils';
import * as S from './styles';

type DialogProps = {
  ariaLabel: string;
  children: React.ReactNode;
  dialogId: string;
  onClose: () => void;
  OverlayComponent?: React.ElementType;
  DialogComponent?: React.ElementType;
  FooterComponent?: React.ElementType;
  ButtonComponent?: React.ElementType;
};

const Dialog = ({
  ariaLabel,
  children,
  dialogId,
  onClose,
  OverlayComponent = S.BaseOverlay,
  DialogComponent = S.BaseDialog,
  FooterComponent = S.BaseFooter,
  ButtonComponent = S.BaseButton,
}: DialogProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    appUtils.lockVerticalScrolling({ isForPortal: true });
    if (buttonRef.current) {
      buttonRef.current.focus();
    }

    return () => {
      appUtils.unlockVerticalScrolling();
    };
  }, []);

  // Esc key will close filter modal
  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    return e.keyCode === 27 && onClose();
  };

  // Clicking away will close filter modal
  const handleClickAway = (e: React.MouseEvent<HTMLDivElement>) => {
    if (ref.current && !ref.current.contains(e.target as Node)) {
      onClose();
    }
  };

  return ReactDOM.createPortal(
    <OverlayComponent
      role="dialog"
      aria-label={ariaLabel}
      aria-modal="true"
      tabIndex={-1}
      onKeyDown={handleOnKeyDown}
      onClick={handleClickAway}
    >
      <FocusLock>
        <DialogComponent id={dialogId} ref={ref}>
          {children}
          <FooterComponent>
            <ButtonComponent onClick={onClose} aria-label="Close modal" ref={buttonRef}>
              Done
            </ButtonComponent>
          </FooterComponent>
        </DialogComponent>
      </FocusLock>
    </OverlayComponent>,
    document.body,
  );
};

export default Dialog;
